import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal, Box, Typography, IconButton } from "@material-ui/core";
import Button from "@material-ui/core/Button";
import { makeStyles } from "@material-ui/core/styles";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorIcon from "@material-ui/icons/Error";
import WarningIcon from "@material-ui/icons/Warning";
import CloseIcon from "@material-ui/icons/Close";
import { setAlertDialog } from "../../js/reducers/alertDialog";

const typeConfig = {
  success: {
    icon: CheckCircleIcon,
    gradient: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
    bg: "#ecfdf5",
    border: "#a7f3d0",
    text: "#059669",
    ring: "#d1fae5",
    shadow: "rgba(16,185,129,0.25)",
  },
  error: {
    icon: ErrorIcon,
    gradient: "linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)",
    bg: "#fef2f2",
    border: "#fecaca",
    text: "#b91c1c",
    ring: "#fee2e2",
    shadow: "rgba(239,68,68,0.25)",
  },
  warning: {
    icon: WarningIcon,
    gradient: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
    bg: "#fffbeb",
    border: "#fde68a",
    text: "#b45309",
    ring: "#fef3c7",
    shadow: "rgba(245,158,11,0.25)",
  },
};

const useStyles = makeStyles((theme) => ({
  "@keyframes cardPop": {
    "0%": { opacity: 0, transform: "translateY(12px) scale(0.98)" },
    "100%": { opacity: 1, transform: "translateY(0) scale(1)" },
  },
  overlay: {
    position: "fixed",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backdropFilter: "blur(4px)",
    backgroundColor: "rgba(15,23,42,0.35)",
    zIndex: 999,
    padding: "1.5rem",
  },
  card: {
    width: "100%",
    maxWidth: "520px",
    backgroundColor: "#fff",
    borderRadius: "16px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.08), 0 12px 40px rgba(0,0,0,0.12)",
    overflow: "hidden",
    fontFamily: "Bosch Sans Regular",
    animation: "$cardPop 0.25s cubic-bezier(0.16, 1, 0.3, 1)",
  },
  /* ── Header ── */
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px 20px",
    borderBottom: "1px solid #E5E7EB",
  },
  dialogTitleLeft: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  dialogIconBox: {
    width: 40,
    height: 40,
    borderRadius: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  dialogIcon: {
    fontSize: "22px !important",
  },
  closeButton: {
    color: "#6B7280",
  },
  dialogTitleText: {
    color: "#0f172a",
    fontWeight: 700,
    fontSize: "16px !important",
    margin: 0,
    letterSpacing: "0.02em",
  },
  /* ── Body ── */
  body: {
    padding: "0 24px 8px",
  },
  messageText: {
    fontSize: "14px !important",
    color: "#475569",
    lineHeight: 1.7,
    margin: 0,
    whiteSpace: "pre-line",
    marginTop: "10px",
  },
  /* ── Footer ── */
  footer: {
    padding: "12px 20px",
    borderTop: "1px solid #e2e8f0",
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px",
  },
  btnCancel: {
    borderRadius: 8,
    fontWeight: 600,
    color: "#0f172a",
    borderColor: "#e2e8f0",
    textTransform: "none",
    // padding: "9px 20px",
    fontSize: "13px !important",
    border: "1px solid",
    transition: "all 0.15s",
    "&:hover": {
      backgroundColor: "#f8fafc",
      borderColor: "#cbd5e1",
    },
  },
  btnConfirm: {
    borderRadius: 8,
    fontWeight: 600,
    textTransform: "none",
    // padding: "9px 24px",
    fontSize: "13px !important",
    border: "none",
    transition: "all 0.15s",
    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
    color: "#fff",
    "&:hover": {
      boxShadow: "0 4px 14px rgba(0,0,0,0.2)",
      transform: "translateY(-1px)",
    },
  },
}));

const AlertDialog = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [open, setOpen] = React.useState(false);
  
  useSelector((state) => {
    if (open == false)
      if (state?.alertDialog?.alertDialogOpen) {
        setOpen(state?.alertDialog?.alertDialogOpen);
      }
  });

  const dialogType = useSelector(
    (state) => state?.alertDialog?.alertDialogType || "success"
  );
  const dialogTitle = useSelector(
    (state) => state?.alertDialog?.alertDialogTitle
  );
  const dialogMessage = useSelector(
    (state) => state?.alertDialog?.alertDialogMessage
  );
  const dialogOkText = useSelector(
    (state) => state?.alertDialog?.alertDialogOkText
  );
  const dialogCancelText = useSelector(
    (state) => state?.alertDialog?.alertDialogCancelText
  );

  const config = typeConfig[dialogType] || typeConfig.success;
  const IconComponent = config.icon;

  const handleClose = () => {
    setOpen(false);
    dispatch(setAlertDialog(false, false));
  };

  const handleOk = () => {
    setOpen(false);
    dispatch(setAlertDialog(false, true));
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <div className={classes.overlay} onClick={handleClose}>
        <div className={classes.card} onClick={(e) => e.stopPropagation()}>

          {/* Header */}
          <Box className={classes.header} style={{ backgroundColor: config.bg }}>
            <Box className={classes.dialogTitleLeft}>
              <Box className={classes.dialogIconBox} style={{ backgroundColor: config.ring }}>
                <IconComponent
                  className={classes.dialogIcon}
                  style={{
                    color: config.text,
                  }}
                />
              </Box>
              <Typography variant="subtitle1">
                <strong>{dialogTitle}</strong>
              </Typography>
            </Box>
          </Box>

          {/* Body */}
          <div className={classes.body}>
            {dialogMessage?.split("\n").map((item, key) => {
              if (key === 0)
                return (
                  <Typography key={key} className={classes.messageText}>
                    {item}
                  </Typography>
                );
              else
                return (
                  <React.Fragment key={key}>
                    <Typography className={classes.messageText}>
                      {item}
                    </Typography>
                  </React.Fragment>
                );
            })}
          </div>

          {/* Footer */}
          <div className={classes.footer}>
            {dialogCancelText && dialogCancelText.length > 0 && (
              <Button
                onClick={handleClose}
                variant="outlined"
                className={classes.btnCancel}
              >
                {dialogCancelText}
              </Button>
            )}
            {dialogOkText && dialogOkText.length > 0 && (
              <Button
                onClick={handleOk}
                variant="contained"
                className={classes.btnConfirm}
                style={{ background: config.gradient }}
              >
                {dialogOkText}
              </Button>
            )}
          </div>

        </div>
      </div>
    </Modal>
  );
};

export default React.memo(AlertDialog);
